import type { TableRow } from "@general-backoffice/core";
import { FormInput, Loading, Modal, notify, TableCard, TableOutput } from "@general-backoffice/core";
import type { Charge, PaginationMeta, SubscriptionRelation, User } from "@investhinks/api-client";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Badge, Button, Card, Col, FormGroup, Row } from "reactstrap";

import { chargesEndpoints, invoicingEndpoints } from "../../api";
import type { RootState } from "../../redux/reducers/session/state";
import { notifyLoadingToError, notifyLoadingToSuccess } from "../../utils/notify/notifyLoadingUpdate";
import { TypedTable } from "../../utils/typedLib";
import UserSelect from "../users/UserSelect";
import { getCurrencyDisplay } from "../utils/currency";
import { getStatusColor } from "../utils/statusColor";
import PaymentDetailsModal from "./PaymentDetailsModal";

// Props interfaces
interface ChargeFilters {
  pm_periodEnd?: {
    $gte?: string;
    $lte?: string;
  };
  pm_status?: {
    $eq?: string;
  };
  "pm_user.id"?: {
    $eq?: number | string;
  };
  [key: string]: unknown;
}

interface ChargesTableCardProps {
  title?: string;
  charges: Charge[];
  isLoading: boolean;
  pagination: PaginationMeta;
  goToPage: (page: number) => void;
  changePageSize: (size: number) => void;
  filters: ChargeFilters;
  setFilters: (filters: ChargeFilters) => void;
  columnsToShow?: string[];
  filtersToShow?: string[];
  compact?: boolean;
  subscription?: SubscriptionRelation | null;
}

interface FilterModalProps {
  isOpen: boolean;
  toggle: () => void;
  filters: ChargeFilters;
  setFilters: (filters: ChargeFilters) => void;
  isLoading: boolean;
  filtersToShow: string[];
}

const statusColor: Record<string, string> = {
  pending: "gray",
  processing: "info",
  completed: "success",
  failed: "danger",
  cancelled: "danger",
};

type ExtendedCharge = Charge & {
  isSelected?: boolean;
  _source?: string;
  pm_user?: { name?: string; email?: string };
  pm_amount?: number;
  pm_currency?: string;
  pm_status?: string;
  pm_invoiceNumber?: string | number;
  pm_periodStart?: string;
  pm_periodEnd?: string;
  pm_refunded?: boolean;
  periodProfit?: number;
};

const ChargesTableCard: React.FC<ChargesTableCardProps> = ({
  title,
  charges,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  filters,
  setFilters,
  columnsToShow = [],
  filtersToShow = [],
  compact = false,
  subscription = null,
}) => {
  const { t } = useTranslation("charges", { keyPrefix: "chargesTableCard" });
  const user = useSelector(({ sessionState }: RootState) => sessionState.user);
  const [selectedChargesId, setSelectedChargesId] = useState<(number | string)[]>([]);
  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);
  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const [isPaymentDetailsOpen, setIsPaymentDetailsOpen] = useState<boolean>(false);

  const selectedCharges = (charges || []).filter(({ id }) => selectedChargesId.includes(id));
  const chargesWithIsSelected = (charges || []).map((ch) =>
    selectedChargesId.includes(ch.id) ? { isSelected: true, ...ch } : ch
  );
  const existsSelectedCharges = selectedCharges.length > 0;

  const onTableChange = (rows: TableRow[] = [], trigger: string) => {
    if (trigger === "selectable") {
      const selectedCharges = rows.filter((ch) => ch.isSelected).map((ch) => ch.id);
      setSelectedChargesId(selectedCharges);
    }
  };

  const downloadSelectedCharges = async () => {
    const loadingNotification = notify.loading(t("downloadAsPdfs.downloading", "Descargando..."));
    try {
      const blob = await chargesEndpoints.downloadInvoices(selectedChargesId.map(Number).filter((n) => !isNaN(n)));
      saveAs(blob, "invoices.zip");
      notifyLoadingToSuccess(loadingNotification, t("downloadAsPdfs.downloaded", "Descargado"));
    } catch {
      notifyLoadingToError(loadingNotification, t("downloadAsPdfs.downloadError", "Error al descargar"));
    }
  };

  const handleViewDetails = (charge: Charge) => {
    setSelectedCharge(charge);
    setIsPaymentDetailsOpen(true);
  };

  const downloadSingleInvoice = async (charge: Charge & { _source?: string }) => {
    const loadingNotification = notify.loading(t("downloadAsPdfs.downloading", "Descargando..."));
    try {
      let blob;
      // Check source to determine which API to use
      if (charge._source === "invoice") {
        // New invoice entity - use invoice endpoint
        blob = await invoicingEndpoints.downloadMyInvoicePDF(charge.id);
      } else {
        // Old charge entity - use charge endpoint
        blob = await chargesEndpoints.downloadInvoice(charge.id);
      }
      saveAs(blob, `invoice_${charge.pm_invoiceNumber}.pdf`);
      notifyLoadingToSuccess(loadingNotification, t("downloadAsPdfs.downloaded", "Descargado"));
    } catch {
      notifyLoadingToError(loadingNotification, t("downloadAsPdfs.downloadError", "Error al descargar"));
    }
  };

  useEffect(() => {
    setSelectedChargesId([]);
  }, [charges]);

  const columns = [
    {
      name: t("columns.user"),
      hidden: !columnsToShow.includes("user"),
      formattedContent: ({ pm_user }: ExtendedCharge) => (
        <>
          <div>{pm_user?.name || pm_user?.email}</div>
          {pm_user?.name && <small>{pm_user?.email}</small>}
        </>
      ),
      isTh: true,
    },
    {
      name: t("columns.period"),
      formattedContent: ({ pm_periodEnd, pm_periodStart }: ExtendedCharge) => {
        if (compact) {
          return (
            <div style={{ fontSize: "0.8rem", lineHeight: 1.3 }}>
              <div>{moment(pm_periodStart).format("DD/MM/YY")}</div>
              <small className="text-muted">{moment(pm_periodEnd).format("DD/MM/YY")}</small>
            </div>
          );
        }
        return moment(pm_periodStart).format("DD/MM/YYYY") + " - " + moment(pm_periodEnd).format("DD/MM/YYYY");
      },
      isTh: true,
    },
    {
      name: t("columns.state"),
      formattedContent: ({ pm_status, pm_refunded }: ExtendedCharge) => (
        <div className="d-flex align-items-center" style={{ gap: "0.25rem" }}>
          <TableOutput.Status
            status={t(`statuses.${pm_status}`, pm_status || "")}
            color={getStatusColor(statusColor, pm_status)}
          />
          {pm_refunded && (
            <Badge color="info" className="ml-1">
              {t("refunded")}
            </Badge>
          )}
        </div>
      ),
      isTh: true,
      hidden: compact,
    },
    {
      name: compact ? t("columns.profitCompact") : t("columns.profits"),
      formattedContent: ({ periodProfit, pm_currency }: ExtendedCharge) => (
        <span style={compact ? { fontSize: "0.85rem" } : {}}>
          {(periodProfit || 0).toFixed(2)} {getCurrencyDisplay(pm_currency)}
        </span>
      ),
    },
    {
      name: compact ? t("columns.feeCompact") : t("columns.commission"),
      formattedContent: ({ pm_amount, pm_currency, pm_status }: ExtendedCharge) => (
        <span
          style={compact ? { fontSize: "0.85rem" } : {}}
          className={pm_status === "completed" ? "text-success" : pm_status === "failed" ? "text-danger" : ""}
        >
          {(pm_amount || 0).toFixed(2)} {getCurrencyDisplay(pm_currency)}
          {compact && (
            <small
              className={`ml-1 ${pm_status === "completed" ? "text-success" : pm_status === "failed" ? "text-danger" : "text-muted"}`}
              title={pm_status ? t(`statuses.${pm_status}`, pm_status) : undefined}
            >
              <span aria-hidden="true">{pm_status === "completed" ? "✓" : pm_status === "failed" ? "✗" : ""}</span>
              <span className="sr-only">{pm_status ? t(`statuses.${pm_status}`, pm_status) : ""}</span>
            </small>
          )}
        </span>
      ),
    },
    {
      name: "",
      formattedContent: (charge: ExtendedCharge) => {
        const hasFailed = charge.pm_status === "failed";
        const detailsLabel = hasFailed ? t("viewFailureDetails") : t("viewPaymentDetails");

        return (
          <div className="d-flex justify-content-end" style={{ gap: "0.5rem" }}>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                downloadSingleInvoice(charge);
              }}
              title={t("downloadInvoice")}
              aria-label={t("downloadInvoice")}
            >
              <i className="fas fa-download" aria-hidden="true" />
            </Button>
            <Button
              size="sm"
              color={hasFailed ? "danger" : "info"}
              outline
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleViewDetails(charge);
              }}
              title={detailsLabel}
              aria-label={detailsLabel}
            >
              <i className={hasFailed ? "fas fa-exclamation-triangle" : "fas fa-info-circle"} aria-hidden="true" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PaymentDetailsModal
        isOpen={isPaymentDetailsOpen}
        toggle={() => setIsPaymentDetailsOpen(false)}
        charge={selectedCharge}
        user={user}
      />

      <FilterModal
        isOpen={isOpenFilters}
        toggle={() => setIsOpenFilters(false)}
        filters={filters}
        setFilters={setFilters}
        isLoading={isLoading}
        filtersToShow={filtersToShow}
      />

      <Card>
        <TableCard.Header
          title={title || t("title")}
          subtitle={
            compact
              ? ""
              : subscription?.pm_currentPeriodEnd
                ? String(
                    t("nextChargeSubtitle", { date: moment(subscription.pm_currentPeriodEnd).format("DD MMM YYYY") })
                  )
                : t("subtitle")
          }
          isLoading={isLoading}
        >
          {!compact && (
            <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
              <Button
                color={existsSelectedCharges ? "default" : "secondary"}
                disabled={!existsSelectedCharges}
                onClick={downloadSelectedCharges}
              >
                <i className="fas fa-file-invoice mr-1" /> {t("downloadButton")}
              </Button>
              <Button onClick={() => setIsOpenFilters(true)}>
                <i className="fas fa-filter" /> {t("filtersButton")}
              </Button>
            </div>
          )}
        </TableCard.Header>
        {isLoading && <Loading.Line />}

        <TypedTable
          selectable={!compact}
          sortable={false}
          columns={columns}
          rows={chargesWithIsSelected}
          onDataChange={onTableChange}
          isLoading={isLoading}
          noContentMessage={t("noContent")}
          rowHeight={compact ? 50 : 70}
          rowAmount={pagination?.pageSize}
        />
        <TableCard.Footer
          amount={pagination?.pageSize}
          setAmount={changePageSize}
          totalEntries={pagination?.total}
          page={pagination?.page - 1}
          onChangePage={(page) => goToPage(page + 1)}
          isLoading={isLoading}
          amountOptions={compact ? [5, 10] : [5, 10, 20, 50, 500]}
        />
      </Card>
    </>
  );
};

function getFilterUser(filters: ChargeFilters): User | null {
  const pmUser = filters.pm_user;
  if (typeof pmUser === "object" && pmUser !== null) {
    return pmUser as User;
  }
  return null;
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, toggle, filters, setFilters, isLoading, filtersToShow }) => {
  const { t } = useTranslation("charges", { keyPrefix: "filterModal" });
  const [localFilters, setLocalFilters] = useState<ChargeFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const resetFilters = () => {
    setLocalFilters((f: ChargeFilters) => {
      const newFilters = { ...f };
      if (newFilters.pm_periodEnd) {
        delete newFilters.pm_periodEnd.$gte;
        delete newFilters.pm_periodEnd.$lte;
        if (Object.keys(newFilters.pm_periodEnd).length === 0) {
          delete newFilters.pm_periodEnd;
        }
      }
      return newFilters;
    });
  };

  const changeNestedFilter = (field: string, operator: string, value: string | undefined) => {
    setLocalFilters((f: ChargeFilters) => {
      const newFilters: ChargeFilters = { ...f };
      if (value === undefined) {
        const fieldValue = newFilters[field];
        if (fieldValue && typeof fieldValue === "object") {
          const fieldObj: Record<string, unknown> = { ...fieldValue };
          delete fieldObj[operator];
          if (Object.keys(fieldObj).length === 0) {
            delete newFilters[field];
          } else {
            newFilters[field] = fieldObj;
          }
        }
      } else {
        const existingField = newFilters[field];
        const existing = typeof existingField === "object" && existingField ? existingField : {};
        newFilters[field] = { ...existing, [operator]: value };
      }
      return newFilters;
    });
  };

  const changeFilter = (key: string, value: unknown) => {
    setLocalFilters((f: ChargeFilters) => ({ ...f, [key]: value }));
  };

  const from = localFilters?.pm_periodEnd?.$gte;
  const to = localFilters?.pm_periodEnd?.$lte;

  return (
    <Modal.FormContainer
      buttonSave={{
        children: t("applyButton"),
      }}
      closeText={t("closeButton")}
      onSubmit={() => {
        setFilters(localFilters);
        toggle();
      }}
      isOpen={isOpen}
      title={t("title")}
      toggleOpen={toggle}
    >
      <div className="d-flex justify-content-end mb-2 mt--4">
        <Button color="warning" onClick={resetFilters} size="sm">
          <i className="fas fa-broom" /> {t("cleanButton")}
        </Button>
      </div>
      <FormInput.DatePicker
        label={t("chargedAfter")}
        key={from}
        value={from ? moment(from) : undefined}
        onChange={(d: moment.Moment | string) => {
          const newFrom = d && typeof d !== "string" ? d.startOf("day").toISOString() : undefined;
          changeNestedFilter("pm_periodEnd", "$gte", newFrom);
        }}
        timeFormat={false}
        dateFormat="DD-MM-YYYY"
        isValidDate={to ? (d: moment.Moment) => d.isSameOrBefore(to) : undefined}
        disabled={isLoading}
      />

      <FormInput.DatePicker
        label={t("chargedBefore")}
        key={to}
        value={to ? moment(to) : undefined}
        onChange={(d: moment.Moment | string) => {
          const newTo = d && typeof d !== "string" ? d.endOf("day").toISOString() : undefined;
          changeNestedFilter("pm_periodEnd", "$lte", newTo);
        }}
        timeFormat={false}
        dateFormat="DD-MM-YYYY"
        isValidDate={from ? (d: moment.Moment) => d.isSameOrAfter(from) : undefined}
        disabled={isLoading}
      />

      {filtersToShow.includes("user") && (
        <Row>
          <Col>
            <FormGroup className="mb--4">
              <UserSelect
                label={t("user")}
                user={getFilterUser(localFilters)}
                setUser={(user) => changeFilter("pm_user", user && !Array.isArray(user) ? user.id : undefined)}
                isDisabled={isLoading}
                isMulti={false}
                role={undefined}
              />
            </FormGroup>
          </Col>
        </Row>
      )}
    </Modal.FormContainer>
  );
};

export default ChargesTableCard;
